import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError, authApi } from "@/lib/auth";

const inputCls =
  "h-11 w-full border border-black/15 bg-paper px-4 text-sm text-ink placeholder:text-ink/35 focus:border-ink focus:outline-none";

export default function ChangePasswordDialog({ open, onClose, forced }) {
  const { user, setUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("La nuova password deve avere almeno 8 caratteri.");
    if (next !== confirm) return toast.error("Le due password non coincidono.");
    setSaving(true);
    try {
      await authApi.post("/auth/change-password", { current_password: current, new_password: next });
      setUser({ ...user, must_change_password: false });
      toast.success("Password aggiornata.");
      setCurrent(""); setNext(""); setConfirm("");
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[75] bg-ink/60 backdrop-blur-sm" onClick={forced ? undefined : onClose} />
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            className="fixed left-1/2 top-1/2 z-[76] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 bg-white p-8 shadow-2xl"
            data-testid="change-password-dialog"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-terracotta">Sicurezza</p>
                <h3 className="mt-2 font-display text-2xl font-extrabold tracking-tighter">
                  {forced ? "Imposta la tua password" : "Cambia password"}
                </h3>
                {forced && (
                  <p className="mt-2 text-xs leading-relaxed text-ink/55">
                    Primo accesso: scegli una password personale per sostituire quella iniziale.
                  </p>
                )}
              </div>
              {!forced && (
                <button onClick={onClose} className="flex h-9 w-9 items-center justify-center border border-black/10 hover:bg-ink hover:text-white" aria-label="Chiudi" data-testid="change-password-close">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Password attuale" className={inputCls} autoComplete="current-password" data-testid="change-password-current" />
              <input type="password" required value={next} onChange={(e) => setNext(e.target.value)} placeholder="Nuova password (min. 8 caratteri)" className={inputCls} autoComplete="new-password" data-testid="change-password-new" />
              <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Ripeti nuova password" className={inputCls} autoComplete="new-password" data-testid="change-password-confirm" />
              <button type="submit" disabled={saving} className="mt-2 h-12 w-full bg-ink font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors hover:bg-cobalt disabled:opacity-50" data-testid="change-password-submit">
                {saving ? "Salvataggio…" : "Salva password"}
              </button>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
